import React, { useState, useEffect } from 'react';
import { authApi } from '../firebase/config.js';
import { ENV } from '../config/env.js';

const API_BASE_URL = ENV.apiBaseUrl;

export default function ShareFolderModal({ folder, onClose }) {
    const [shareUrl, setShareUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Auto-generate share link on open
        generateLink();
    }, []);

    const generateLink = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await authApi(`${API_BASE_URL}/api/folders/${folder.id}/share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: folder.name, cardCount: folder.cardCount ?? (folder.cards || []).length }),
            });
            const url = `${window.location.origin}/s/${data.shareId}`;
            setShareUrl(url);
        } catch (err) {
            setError(err.message || 'Failed to create share link.');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback
            const el = document.createElement('input');
            el.value = shareUrl;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900/98 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-fuchsia-500/20 border border-fuchsia-500/30 flex items-center justify-center">
                            <svg className="h-4 w-4 text-fuchsia-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-white">Share Study Set</h2>
                            <p className="text-xs text-slate-400">{folder.name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="px-5 py-5 space-y-4">
                    <div className="rounded-2xl border border-white/8 bg-slate-800/40 p-4 text-center">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-400 mb-1">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            {folder.cardCount ?? (folder.cards || []).length} cards
                        </div>
                        <p className="text-xs text-slate-500">Anyone with this link can view and study this set (read-only)</p>
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-4">
                            <div className="h-5 w-5 rounded-full border-2 border-fuchsia-400/30 border-t-fuchsia-400 animate-spin" />
                            <span className="ml-2.5 text-sm text-slate-400">Generating link…</span>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-3">
                            <p className="text-sm text-rose-300">{error}</p>
                            <button onClick={generateLink} className="mt-2 text-xs text-rose-400 underline">Try again</button>
                        </div>
                    )}

                    {shareUrl && !loading && (
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-400">Share link</label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0 rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2.5">
                                    <p className="text-xs text-slate-300 truncate font-mono">{shareUrl}</p>
                                </div>
                                <button onClick={copyLink}
                                    className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-semibold transition ${copied ? 'bg-emerald-600 text-white' : 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white'}`}>
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl bg-white/3 border border-white/5 px-4 py-3">
                        <p className="text-xs text-slate-400 font-medium mb-1">Recipients can:</p>
                        <ul className="space-y-1">
                            {['View all cards in the set', 'Study in flashcard mode', 'Use Learn mode'].map(item => (
                                <li key={item} className="flex items-center gap-2 text-xs text-slate-500">
                                    <span className="text-emerald-400">✓</span>{item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-5 pb-5">
                    <button onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
